import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import api from "../utils/api";
import { User, Permission } from "../types";

export interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  permissions: Permission[];
  hasPermission: (permission: Permission) => boolean;
  hasAnyPermission: (permissions: Permission[]) => boolean;
  hasAllPermissions: (permissions: Permission[]) => boolean;
  login: (token: string, user: User) => void;
  logout: () => void;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  // Consolidate state to prevent partial updates and multiple re-renders
  const [state, setState] = useState<AuthState>({
    user: null,
    token: null,
    isLoading: true,
  });

  // Initialize auth state on mount, then revalidate against the API so a
  // demoted / stale localStorage user cannot keep a broken admin session.
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedToken = localStorage.getItem("token");
        const storedUser = localStorage.getItem("user");

        if (storedToken && storedUser) {
          try {
            const parsedUser = JSON.parse(storedUser) as User;
            api.setAuthToken(storedToken);

            // Optimistic restore so the shell can render immediately
            setState({
              user: parsedUser,
              token: storedToken,
              isLoading: true,
            });

            // Revalidate with server (role / permissions / isSuperAdmin)
            try {
              const me = await api.get("/api/users/me");
              const freshUser = (me?.user || me?.data?.user || me) as User;
              const freshId =
                (freshUser as any)?._id || (freshUser as any)?.id;
              if (freshUser && freshId) {
                if (freshUser.role !== "admin") {
                  localStorage.removeItem("token");
                  localStorage.removeItem("user");
                  api.setAuthToken(null);
                  setState({ user: null, token: null, isLoading: false });
                  return;
                }
                localStorage.setItem("user", JSON.stringify(freshUser));
                setState({
                  user: freshUser,
                  token: storedToken,
                  isLoading: false,
                });
                return;
              }
            } catch {
              // Network / 401: fall through to stored credentials; 401 handler
              // will logout if the token is truly invalid.
            }

            setState({
              user: parsedUser,
              token: storedToken,
              isLoading: false,
            });
          } catch (jsonError) {
            console.error("Failed to parse stored user data:", jsonError);
            throw new Error("Invalid user storage");
          }
        } else {
          setState((prev) => ({ ...prev, isLoading: false }));
        }
      } catch {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        api.setAuthToken(null);
        setState({
          user: null,
          token: null,
          isLoading: false,
        });
      }
    };

    void initializeAuth();
  }, []);

  const login = useCallback((newToken: string, newUser: User) => {
    // Update API client first (handles token storage)
    api.setAuthToken(newToken);

    // Store user data locally
    localStorage.setItem("user", JSON.stringify(newUser));
    localStorage.setItem("token", newToken);

    // Update state
    setState({
      user: newUser,
      token: newToken,
      isLoading: false,
    });
  }, []);

  const logout = useCallback(() => {
    // Update API client (handles token removal)
    api.setAuthToken(null);

    // Remove user data locally
    localStorage.removeItem("user");
    localStorage.removeItem("token");

    // Update state
    setState({
      user: null,
      token: null,
      isLoading: false,
    });
  }, []);

  // Listen for global 401 events from api.js
  useEffect(() => {
    const handleUnauthorized = () => {
      // Only logout if currently authenticated to avoid loops
      logout();
    };

    window.addEventListener("auth:unauthorized", handleUnauthorized);
    return () =>
      window.removeEventListener("auth:unauthorized", handleUnauthorized);
  }, [logout]);

  // Derive authenticated status and admin status
  const isTargetUserAdmin = state.user?.role === "admin";
  const isAuthenticated = !!state.user && !!state.token && isTargetUserAdmin;
  const isSuperAdmin = state.user?.isSuperAdmin ?? false;
  const permissions = state.user?.permissions ?? [];

  // Permission checking methods
  const hasPermission = useCallback(
    (permission: Permission): boolean => {
      if (isSuperAdmin) return true;
      return permissions.includes(permission);
    },
    [isSuperAdmin, permissions]
  );

  const hasAnyPermission = useCallback(
    (requiredPermissions: Permission[]): boolean => {
      if (isSuperAdmin) return true;
      return requiredPermissions.some((perm) => permissions.includes(perm));
    },
    [isSuperAdmin, permissions]
  );

  const hasAllPermissions = useCallback(
    (requiredPermissions: Permission[]): boolean => {
      if (isSuperAdmin) return true;
      return requiredPermissions.every((perm) => permissions.includes(perm));
    },
    [isSuperAdmin, permissions]
  );

  const value = useMemo(
    () => ({
      ...state,
      isAuthenticated,
      isAdmin: isTargetUserAdmin,
      isSuperAdmin,
      permissions,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      login,
      logout,
    }),
    [
      state,
      isAuthenticated,
      isTargetUserAdmin,
      isSuperAdmin,
      permissions,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      login,
      logout,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
